import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Check, EyeOff, Save, Star } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EMPTY = { name: "", location: "", text: "", rating: 5, product: "", lang: "en", approved: true, permission: false, order: 0 };

const field = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "8px",
  padding: "8px 10px",
  color: "white",
  fontSize: "0.85rem",
  width: "100%",
};
const labelStyle = { color: "rgba(255,255,255,0.5)", fontSize: "0.72rem", display: "block", marginBottom: "4px" };

function Row({ item, onSave, onDelete, busy }) {
  const [draft, setDraft] = useState(item);
  const dirty = JSON.stringify(draft) !== JSON.stringify(item);
  useEffect(() => setDraft(item), [item]);

  return (
    <div data-testid={`admin-testimonial-${item.id}`} className="glass-card" style={{ padding: "16px", marginBottom: "12px", border: item.approved ? "1px solid rgba(134,239,172,0.25)" : "1px solid rgba(249,115,22,0.25)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px", marginBottom: "10px" }}>
        <div>
          <label style={labelStyle}>Name</label>
          <input data-testid="testimonial-name-input" style={field} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} />
        </div>
        <div>
          <label style={labelStyle}>Location</label>
          <input data-testid="testimonial-location-input" style={field} value={draft.location || ""} onChange={e => setDraft({ ...draft, location: e.target.value })} />
        </div>
        <div>
          <label style={labelStyle}>Product used</label>
          <input data-testid="testimonial-product-input" style={field} value={draft.product || ""} onChange={e => setDraft({ ...draft, product: e.target.value })} />
        </div>
        <div>
          <label style={labelStyle}>Rating</label>
          <select data-testid="testimonial-rating-input" style={field} value={draft.rating} onChange={e => setDraft({ ...draft, rating: Number(e.target.value) })}>
            {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{`${n} stars`}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Language</label>
          <select data-testid="testimonial-lang-input" style={field} value={draft.lang} onChange={e => setDraft({ ...draft, lang: e.target.value })}>
            <option value="en">English</option>
            <option value="hi">हिन्दी</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Order</label>
          <input data-testid="testimonial-order-input" type="number" style={field} value={draft.order} onChange={e => setDraft({ ...draft, order: Number(e.target.value) })} />
        </div>
      </div>
      <label style={labelStyle}>Review</label>
      <textarea data-testid="testimonial-text-input" rows={3} style={{ ...field, resize: "vertical", marginBottom: "10px" }} value={draft.text} onChange={e => setDraft({ ...draft, text: e.target.value })} />
      <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
        <label style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
          <input data-testid="testimonial-permission-input" type="checkbox" checked={!!draft.permission} onChange={e => setDraft({ ...draft, permission: e.target.checked })} />
          Permission to publish on record
        </label>
        <span style={{
          padding: "2px 10px", borderRadius: "999px", fontSize: "0.72rem", fontWeight: 700,
          background: item.approved ? "rgba(34,197,94,0.15)" : "rgba(249,115,22,0.15)",
          color: item.approved ? "#86efac" : "#fdba74",
        }}>{item.approved ? "Live on site" : "Hidden"}</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
          <button data-testid="testimonial-toggle-approve" disabled={busy} onClick={() => onSave(item.id, { approved: !item.approved })}
            className={item.approved ? "btn-purple" : "btn-gold"} style={{ padding: "7px 14px", fontSize: "0.78rem" }}>
            {item.approved ? <><EyeOff size={13} /> Unapprove</> : <><Check size={13} /> Approve</>}
          </button>
          <button data-testid="testimonial-save-btn" disabled={busy || !dirty} onClick={() => onSave(item.id, draft)}
            className="btn-gold" style={{ padding: "7px 14px", fontSize: "0.78rem", opacity: dirty ? 1 : 0.4 }}>
            <Save size={13} /> Save
          </button>
          <button data-testid="testimonial-delete-btn" disabled={busy} onClick={() => onDelete(item.id)}
            style={{ padding: "7px 12px", fontSize: "0.78rem", background: "rgba(239,68,68,0.14)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "999px", cursor: "pointer" }}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY);

  const load = async () => {
    setError("");
    try {
      const r = await axios.get(`${API}/admin/testimonials`, { withCredentials: true });
      setItems(r.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load testimonials.");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (id, patch) => {
    setBusy(true); setError("");
    try {
      await axios.patch(`${API}/admin/testimonials/${id}`, patch, { withCredentials: true });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    setBusy(true); setError("");
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, { withCredentials: true });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Delete failed.");
    } finally { setBusy(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/admin/testimonials`, draft, { withCredentials: true });
      setDraft(EMPTY); setAdding(false);
      await load();
    } catch (e2) {
      setError(e2.response?.data?.detail || "Could not add the review.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-testimonials">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
        <div>
          <h3 className="font-cinzel" style={{ color: "#FFD166", fontSize: "1.05rem" }}>
            <Star size={15} style={{ display: "inline", marginRight: "6px" }} /> Customer reviews ({items.length})
          </h3>
          <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.8rem", marginTop: "4px" }}>
            Only approved reviews appear on the homepage, matched to the visitor&apos;s language.
          </p>
        </div>
        <button data-testid="testimonial-add-toggle" onClick={() => setAdding(a => !a)} className="btn-gold" style={{ padding: "9px 18px", fontSize: "0.84rem" }}>
          <Plus size={14} /> {adding ? "Cancel" : "Add review"}
        </button>
      </div>

      {error && <p data-testid="admin-testimonials-error" role="alert" style={{ color: "#fca5a5", fontSize: "0.85rem", marginBottom: "12px" }}>{error}</p>}

      {adding && (
        <form onSubmit={create} data-testid="testimonial-add-form" className="glass-card" style={{ padding: "16px", marginBottom: "18px", border: "1px solid rgba(255,209,102,0.3)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px", marginBottom: "10px" }}>
            <div>
              <label style={labelStyle}>Name *</label>
              <input data-testid="new-testimonial-name" required style={field} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Location</label>
              <input data-testid="new-testimonial-location" style={field} value={draft.location} onChange={e => setDraft({ ...draft, location: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Product used</label>
              <input data-testid="new-testimonial-product" style={field} value={draft.product} onChange={e => setDraft({ ...draft, product: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Rating</label>
              <select data-testid="new-testimonial-rating" style={field} value={draft.rating} onChange={e => setDraft({ ...draft, rating: Number(e.target.value) })}>
                {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{`${n} stars`}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Language</label>
              <select data-testid="new-testimonial-lang" style={field} value={draft.lang} onChange={e => setDraft({ ...draft, lang: e.target.value })}>
                <option value="en">English</option>
                <option value="hi">हिन्दी</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Order</label>
              <input data-testid="new-testimonial-order" type="number" style={field} value={draft.order} onChange={e => setDraft({ ...draft, order: Number(e.target.value) })} />
            </div>
          </div>
          <label style={labelStyle}>Review *</label>
          <textarea data-testid="new-testimonial-text" required rows={3} style={{ ...field, resize: "vertical", marginBottom: "10px" }} value={draft.text} onChange={e => setDraft({ ...draft, text: e.target.value })} />
          <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
            <label style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
              <input data-testid="new-testimonial-approved" type="checkbox" checked={draft.approved} onChange={e => setDraft({ ...draft, approved: e.target.checked })} />
              Publish immediately
            </label>
            <label style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
              <input data-testid="new-testimonial-permission" type="checkbox" checked={draft.permission} onChange={e => setDraft({ ...draft, permission: e.target.checked })} />
              Permission to publish on record
            </label>
            <button data-testid="new-testimonial-submit" type="submit" disabled={busy} className="btn-gold" style={{ marginLeft: "auto", padding: "9px 20px", fontSize: "0.84rem" }}>
              {busy ? "Saving…" : "Save review"}
            </button>
          </div>
        </form>
      )}

      {items.length === 0 ? (
        <p data-testid="admin-testimonials-empty" style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.88rem" }}>
          No reviews yet. Add one above — the homepage section stays hidden until a review is approved.
        </p>
      ) : (
        items.map(item => <Row key={item.id} item={item} onSave={save} onDelete={remove} busy={busy} />)
      )}
    </div>
  );
}
